import { useEffect, useState, useCallback } from 'react'

export type BorderStyle = 'solid' | 'dashed' | 'dotted' | 'dashDot' | 'cloudy'

export interface FreeTextStyle {
  fontFamily: string
  fontSize: number
  fontColor: string
  textAlign: 'left' | 'center' | 'right'
  verticalAlign: 'top' | 'middle' | 'bottom'
}

export interface StyleState {
  fillColor: string
  strokeColor: string
  strokeWidth: number
  opacity: number
  borderStyle: BorderStyle
  rotation: number
  freeTextStyle: FreeTextStyle
}

const defaultStyle: StyleState = {
  fillColor: '#000000',
  strokeColor: '#FF0000',
  strokeWidth: 2,
  opacity: 0.8,
  borderStyle: 'solid',
  rotation: 0,
  freeTextStyle: {
    fontFamily: 'Arial',
    fontSize: 12,
    fontColor: '#000000',
    textAlign: 'left',
    verticalAlign: 'top',
  },
}

const highlightDefaults: Partial<StyleState> = {
  strokeColor: '#FFFF00',
  opacity: 0.4,
}

const inkDefaults: Partial<StyleState> = {
  strokeColor: '#FF0000',
  strokeWidth: 2,
  opacity: 1.0,
}

const inkHighlighterDefaults: Partial<StyleState> = {
  strokeColor: '#FFD700',
  strokeWidth: 20,
  opacity: 0.4,
}

const shapeDefaults: Partial<StyleState> = {
  fillColor: '#000000',
  strokeColor: '#000000',
  strokeWidth: 2,
  opacity: 0.8,
}

const freeTextDefaults: Partial<StyleState> = {
  opacity: 1.0,
  freeTextStyle: {
    fontFamily: 'Arial',
    fontSize: 12,
    fontColor: '#000000',
    textAlign: 'left',
    verticalAlign: 'top',
  },
}

const stampDefaults: Partial<StyleState> = {
  opacity: 1.0,
}

const lineDefaults: Partial<StyleState> = {
  strokeColor: '#000000',
  strokeWidth: 2,
  opacity: 1.0,
}

export const TOOL_DEFAULTS: Record<string, Partial<StyleState>> = {
  highlight: highlightDefaults,
  strikeout: { strokeColor: '#EF4444', opacity: 1.0 },
  underline: { strokeColor: '#22C55E', opacity: 1.0 },
  squiggly: { strokeColor: '#EAB308', opacity: 1.0 },
  ink: inkDefaults,
  inkHighlighter: inkHighlighterDefaults,
  freeText: freeTextDefaults,
  stamp: stampDefaults,
  square: shapeDefaults,
  circle: shapeDefaults,
  rectangle: shapeDefaults,
  oval: shapeDefaults,
  line: lineDefaults,
  lineArrow: lineDefaults,
  polygon: shapeDefaults,
  polyline: { strokeColor: '#000000', strokeWidth: 2, opacity: 1.0 },
  redact: { fillColor: '#000000', strokeColor: '#000000', strokeWidth: 2, opacity: 0.8 },
}

let currentStyle: StyleState = { ...defaultStyle }
const styleListeners = new Set<(style: StyleState) => void>()

function mergeDefaults(base: StyleState, partial?: Partial<StyleState>): StyleState {
  if (!partial) return { ...base }
  return {
    ...base,
    ...partial,
    freeTextStyle: partial.freeTextStyle
      ? { ...base.freeTextStyle, ...partial.freeTextStyle }
      : { ...base.freeTextStyle },
  }
}

export function getCurrentStyle(): StyleState {
  return { ...currentStyle }
}

export function setStyleUpdates(partial: Partial<StyleState>) {
  currentStyle = {
    ...currentStyle,
    ...partial,
    freeTextStyle: partial.freeTextStyle
      ? { ...currentStyle.freeTextStyle, ...partial.freeTextStyle }
      : { ...currentStyle.freeTextStyle },
  }
  styleListeners.forEach((l) => l(currentStyle))
}

export function applyToolDefaults(toolId: string | null) {
  if (!toolId) {
    setStyleUpdates(defaultStyle)
    return
  }
  const toolKey = Object.keys(TOOL_DEFAULTS).find((k) => k.toLowerCase() === toolId.toLowerCase())
  if (!toolKey || !TOOL_DEFAULTS[toolKey]) {
    setStyleUpdates(defaultStyle)
    return
  }
  currentStyle = mergeDefaults(currentStyle, TOOL_DEFAULTS[toolKey])
  styleListeners.forEach((l) => l(currentStyle))
}

export function useStyleState() {
  const [style, setStyleState] = useState<StyleState>(() => ({ ...currentStyle }))

  useEffect(() => {
    const listener = (s: StyleState) => {
      setStyleState({ ...s })
    }
    styleListeners.add(listener)
    setStyleState({ ...currentStyle })
    return () => {
      styleListeners.delete(listener)
    }
  }, [])

  const setFillColor = useCallback((color: string) => setStyleUpdates({ fillColor: color }), [])
  const setStrokeColor = useCallback((color: string) => setStyleUpdates({ strokeColor: color }), [])
  const setStrokeWidth = useCallback((width: number) => setStyleUpdates({ strokeWidth: width }), [])
  const setOpacity = useCallback((opacity: number) => setStyleUpdates({ opacity }), [])
  const setBorderStyle = useCallback((borderStyle: BorderStyle) => setStyleUpdates({ borderStyle }), [])
  const setRotation = useCallback((rotation: number) => setStyleUpdates({ rotation }), [])
  const rotateClockwise = useCallback(() => {
    setStyleUpdates({ rotation: (currentStyle.rotation + 90) % 360 })
  }, [])
  const rotateCounterClockwise = useCallback(() => {
    setStyleUpdates({ rotation: (currentStyle.rotation - 90 + 360) % 360 })
  }, [])
  const setFreeTextStyle = useCallback(
    (freeTextStyle: Partial<FreeTextStyle>) =>
      setStyleUpdates({
        freeTextStyle: { ...currentStyle.freeTextStyle, ...freeTextStyle },
      }),
    [],
  )
  const resetStyle = useCallback(() => setStyleUpdates(defaultStyle), [])

  return {
    style,
    setFillColor,
    setStrokeColor,
    setStrokeWidth,
    setOpacity,
    setBorderStyle,
    setRotation,
    rotateClockwise,
    rotateCounterClockwise,
    setFreeTextStyle,
    resetStyle,
  }
}

export function resolveDashArray(borderStyle: BorderStyle, strokeWidth: number): number[] | undefined {
  switch (borderStyle) {
    case 'dashed':
      return [Math.max(3, strokeWidth * 3), Math.max(3, strokeWidth * 2)]
    case 'dotted':
      return [Math.max(1, strokeWidth), Math.max(2, strokeWidth * 2)]
    case 'dashDot':
      return [Math.max(4, strokeWidth * 4), Math.max(2, strokeWidth * 2), Math.max(1, strokeWidth), Math.max(2, strokeWidth * 2)]
    default:
      return undefined
  }
}
